import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaSearch, FaHandshake, FaLightbulb, FaSeedling } from 'react-icons/fa';

const AboutSection = styled.section`
  background-color: ${props => props.theme.colors.white};
`;

const SectionContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.darkGray};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
  }
`;

const MissionContainer = styled(motion.div)`
  text-align: center;
  max-width: 800px;
  margin: 0 auto ${props => props.theme.spacing.xl};
`;

const MissionStatement = styled.p`
  font-size: ${props => props.theme.fontSizes.xlarge};
  color: ${props => props.theme.colors.darkGray};
  font-weight: 300;
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.md};
  
  strong {
    font-weight: 600;
    color: ${props => props.theme.colors.darkGray};
  }
`;

const ValuesContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ValueIcon = styled(motion.div)`
  width: 50px;
  height: 50px;
  background-color: ${props => props.theme.colors.darkGray};
  color: ${props => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin: 0 auto;
  font-size: 20px;
  transition: all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const ValueCard = styled(motion.div)`
  position: relative;
  width: 240px;
  height: 180px;
  background-color: ${props => props.theme.colors.offWhite};
  border-radius: ${props => props.theme.borderRadius.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  perspective: 1000px;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease-in-out;
  border: 2px solid transparent;
  margin: 0 auto;
  
  &::before {
    content: "";
    display: block;
    width: 0px;
    height: 86%;
    position: absolute;
    top: 7%;
    left: 0%;
    opacity: 0;
    background: linear-gradient(90deg, transparent, ${props => props.theme.colors.accent}, transparent);
    box-shadow: 0 0 50px 30px ${props => props.theme.colors.accent};
    transform: skewX(-20deg);
    transition: all 0.5s ease-in-out;
    z-index: 1;
  }
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 40px 10px rgba(61, 90, 254, 0.3);
    background: rgba(61, 90, 254, 0.05);
    border: 2px solid ${props => props.theme.colors.accent};
  }
  
  &:hover::before {
    animation: neon-shine 0.6s 0s linear;
  }
  
  @keyframes neon-shine {
    from {
      opacity: 0;
      left: 0%;
    }

    50% {
      opacity: 1;
    }

    to {
      opacity: 0;
      left: 100%;
    }
  }
`;

const CardFront = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.md};
  transition: all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  z-index: 2;

  ${props => props.theme.breakpoints.mobile} {
    gap: ${props => props.theme.spacing.sm};
  }
  
  ${ValueCard}:hover & {
    transform: scale(0);
    opacity: 0;
  }
`;

const ValueCardContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: ${props => props.theme.spacing.lg};
  box-sizing: border-box;
  background-color: transparent;
  transform: rotateX(-90deg);
  transform-origin: bottom;
  transition: all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2;
  
  ${ValueCard}:hover & {
    transform: rotateX(0deg);
  }
`;

const ValueTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  color: ${props => props.theme.colors.darkGray};
  font-weight: 700;
  text-align: center;
  
  ${ValueCard}:hover & {
    color: ${props => props.theme.colors.accent};
  }
`;

const ValueDescription = styled.p`
  margin: ${props => props.theme.spacing.sm} 0 0;
  font-size: 14px;
  color: ${props => props.theme.colors.gray};
  line-height: 1.4;
`;

const About = () => {
  const [missionRef, missionInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const [valuesRef, valuesInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({ 
      opacity: 1, 
      y: 0, 
      transition: { 
        delay: 0.1 * i,
        duration: 0.5
      }
    })
  };
  
  const iconVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { 
        type: "spring",
        stiffness: 300,
        damping: 10,
        duration: 0.6
      }
    },
    hover: { 
      scale: 1.1,
      rotate: [0, 10, -10, 0],
      transition: { 
        scale: {
          type: "spring",
          stiffness: 300,
          damping: 10
        },
        rotate: {
          type: "tween",
          duration: 0.5,
          ease: "easeInOut"
        }
      }
    }
  };
  
  return (
    <AboutSection id="about">
      <SectionContainer>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
           Our Mission
        </SectionTitle>
        
        <MissionContainer
          ref={missionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={missionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.7 }}
        >
          <MissionStatement>
            Our mission is to <strong>transform businesses</strong> through innovative technology  that drive growth, efficiency and success - creating a <strong>better world</strong> through progress and meaningful experiences with <strong>computers and technology</strong>.
          </MissionStatement>
        </MissionContainer>
        
        <ValuesContainer
          ref={valuesRef}
          initial="hidden"
          animate={valuesInView ? "visible" : "hidden"}
        >
          <ValueCard
            variants={cardVariants}
            custom={0}
          >
            <CardFront>
              <ValueIcon
                initial="hidden"
                animate="visible"
                whileHover="hover"
                variants={iconVariants}
              >
                <FaSearch />
              </ValueIcon>
              <ValueTitle>Innovation</ValueTitle>
            </CardFront>
            <ValueCardContent>
              <ValueTitle>Innovation</ValueTitle>
              <ValueDescription>We're constantly exploring new technologies and methodologies to deliver cutting-edge solutions.</ValueDescription>
            </ValueCardContent>
          </ValueCard>
          
          <ValueCard
            variants={cardVariants}
            custom={1}
          >
            <CardFront>
              <ValueIcon
                initial="hidden"
                animate="visible"
                whileHover="hover"
                variants={iconVariants}
              >
                <FaHandshake />
              </ValueIcon>
              <ValueTitle>Collaboration</ValueTitle>
            </CardFront>
            <ValueCardContent>
              <ValueTitle>Collaboration</ValueTitle>
              <ValueDescription>We work closely with our clients, fostering partnerships that drive mutual success.</ValueDescription>
            </ValueCardContent>
          </ValueCard>
          
          <ValueCard
            variants={cardVariants}
            custom={2}
          >
            <CardFront>
              <ValueIcon
                initial="hidden"
                animate="visible"
                whileHover="hover"
                variants={iconVariants}
              >
                <FaLightbulb />
              </ValueIcon>
              <ValueTitle>Excellence</ValueTitle>
            </CardFront>
            <ValueCardContent>
              <ValueTitle>Excellence</ValueTitle>
              <ValueDescription>We're committed to delivering exceptional quality in everything we create.</ValueDescription>
            </ValueCardContent>
          </ValueCard>
          
          <ValueCard
            variants={cardVariants}
            custom={3}
          >
            <CardFront>
              <ValueIcon
                initial="hidden"
                animate="visible"
                whileHover="hover"
                variants={iconVariants}
              >
                <FaSeedling />
              </ValueIcon>
              <ValueTitle>Growth</ValueTitle>
            </CardFront>
            <ValueCardContent>
              <ValueTitle>Growth</ValueTitle>
              <ValueDescription>We invest in continuous learning and development for our team and technology.</ValueDescription>
            </ValueCardContent>
          </ValueCard>
        </ValuesContainer>
      </SectionContainer>
    </AboutSection>
  );
};

export default About; 